import React, { memo, useCallback } from 'react';
import { useDispatch, useMappedState } from 'redux-react-hook';
import { withRouter } from 'react-router-dom';
import { ReactSVG } from 'react-svg';

import {
  closeChatChannel,
  getMessageList,
  minifyChatChannel,
  openChatChannel,
  selectChatChannel,
} from '../actions';
import {
  EnumAppType,
  EnumChatAppType,
  EnumPage,
  findEnumById,
} from '../../../constants/enum';
import InputChatText from './InputChatText';
import { useHandlerInputChatText, useScrollMessageList } from '../hooks';
import MessageList from './MessageList';
import { openDialogGeneric } from '../../../redux/genericAction';
import { trans } from '../../../localization';

import CloseSvgIcon from '../../../assets/common/svg/close.svg'

const MiniChatChannel = ({
  id,
  appType,
  isActive,
  hasUnreadMessage,
  handleCloseChatChannel,
  handleOpenChatChannel,
}) => {
  const enumAppType = findEnumById(EnumChatAppType, appType);
  return (
    <div
      className={`mini-chat-channel-wrapper ${
        isActive ? 'channel-active' : ''
      }`}
      onClick={handleOpenChatChannel}
    >
      <div className="avatar">
        <i className={enumAppType.icon} />
      </div>
      <span
        className={`order-id font-weight-bold ${
          hasUnreadMessage ? 'has-new-message blink' : ''
        }`}
      >
        #{id}
      </span>
      <ReactSVG
        src={CloseSvgIcon}
        wrapper="span"
        beforeInjection={(svg) => {
          svg.setAttribute('style', `width: 10px;height:10px;margin:0 5px 0 0;color:#3c3c3c;`)
        }}
        onClick={handleCloseChatChannel}
      />
    </div>
  );
};

const ChatHeader = memo(
  withRouter(
    ({
      id,
      appType,
      isActive,
      handleMinifyChatChannel,
      handleCloseChatChannel,
      isHasUnreadMessage,
      history,
    }) => {
      const dispatch = useDispatch();
      const enumAppType = findEnumById(EnumChatAppType, appType);
      const handleClickTitle = (e) => {
        e.stopPropagation();
        switch (appType) {
          case EnumChatAppType.NOW_SHIP.id: {
            history.push('/ship/order-management');
            setTimeout(
              () =>
                dispatch(
                  openDialogGeneric(EnumPage.NOW_SHIP_ORDER_MANAGEMENT.id, id),
                ),
              100,
            );
            break;
          }
          default:
        }
      };
      return (
        <div
          className={`chat-channel-header ${isActive ? 'channel-active' : ''} ${
            isHasUnreadMessage ? 'has-new-message-bg' : ''
          }`}
          onClick={(e) => {
            e.stopPropagation();
            handleMinifyChatChannel();
          }}
        >
          <div className="avatar">
            <i className={enumAppType.icon} />
          </div>
          <div className="info">
            <div className="name">
              <div className="order-id" onClick={handleClickTitle}>
                Order <span className="font-weight-bold">{id}</span>
              </div>
              <div className="app-type">{enumAppType.label}</div>
            </div>
            <div className="action">
              <ReactSVG
                src={CloseSvgIcon}
                wrapper="span"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseChatChannel();
                }} 
                beforeInjection={(svg) => {
                  svg.setAttribute('style', `width: 14px;height:14px;margin:0 5px 0 0;color:#3c3c3c;`)
                  svg.setAttribute('title',trans('nex_close_tab'))
                }}
              />
              <span className="icon icon-arrow-thin" />
            </div>
          </div>
        </div>
      );
    },
  ),
);

const ChatTabSelect = ({ appType }) => {
  if (appType === EnumAppType.NOW_MERCHANT_APP.id) {
    return null;
  }
  return <div />;
};

const ChatChannel = memo(({ id, isActive }) => {
  const dispatch = useDispatch();
  const mapState = useCallback(state => ({
    chatChannelData: state.chat.chatChannelData[id],
  }), [id]);
  const { chatChannelData } = useMappedState(mapState);
  const {
    appType,
    isDisabled,
    isOpenFull,
    currentChatTab,
    chatTabData,
  } = chatChannelData;
  const currentChatTabData = chatTabData[currentChatTab];
  const {
    hasMore,
    isLoading,
    messages,
    userSeenStatus,
    hasUnreadMessage,
  } = currentChatTabData;
  const handleSelectChatChannel = useCallback(
    () => dispatch(selectChatChannel(id)),
    [dispatch, id],
  );
  const handleCloseChatChannel = useCallback(
    () => dispatch(closeChatChannel(id)),
    [dispatch, id],
  );
  const handleOpenChatChannel = useCallback(
    () => dispatch(openChatChannel(id)),
    [dispatch, id],
  );
  const handleToggleMinifyChatChannel = useCallback(
    () => dispatch(minifyChatChannel(id)),
    [dispatch, id],
  );
  const handleGetMessageList = useCallback(
    (fromMessageId) => {
      dispatch(getMessageList(appType, id, currentChatTab, fromMessageId));
    },
    [dispatch, appType, id, currentChatTab],
  );

  // handler for InputChatText
  const {
    draftMessage,
    handleClearUnreadMessage,
    handleChangeDraftMessage,
    handleSendMessage,
    handleFocusChatText,
    handleBlurChatText,
  } = useHandlerInputChatText({
    appType,
    id,
    currentChatTab,
    currentChatTabData,
  });

  // scroll message list to suitable position
  const {
    messageListRef,
    scrollNewMessageRef,
    scrollBottomRef,
    scrollToBottom,
  } = useScrollMessageList({
    isOpenFull,
    currentChatTab,
    currentChatTabData,
  });

  return isOpenFull ? (
    <div className="chat-channel-wrapper" onClick={handleSelectChatChannel}>
      <ChatHeader
        appType={appType}
        id={id}
        isActive={isActive}
        isHasUnreadMessage={hasUnreadMessage}
        handleCloseChatChannel={handleCloseChatChannel}
        handleMinifyChatChannel={handleToggleMinifyChatChannel}
      />
      <ChatTabSelect appType={appType} />
      <div className="chat-message-list-input-wrapper">
        <MessageList
          key={currentChatTab}
          messageListRef={messageListRef}
          scrollNewMessageRef={scrollNewMessageRef}
          scrollBottomRef={scrollBottomRef}
          appType={appType}
          hasMore={hasMore}
          isLoading={isLoading}
          messages={messages}
          hasUnreadMessage={hasUnreadMessage}
          userSeenStatus={userSeenStatus}
          handleGetMessageList={handleGetMessageList}
        />
        <InputChatText
          appType={appType}
          id={id}
          scrollToBottom={scrollToBottom}
          isDisabled={isDisabled}
          draftMessage={draftMessage}
          handleChangeDraftMessage={handleChangeDraftMessage}
          handleClearUnreadMessage={handleClearUnreadMessage}
          handleSendMessage={handleSendMessage}
          handleFocusChatText={handleFocusChatText}
          handleBlurChatText={handleBlurChatText}
        />
      </div>
    </div>
  ) : (
    <MiniChatChannel
      id={id}
      isActive={isActive}
      appType={appType}
      hasUnreadMessage={hasUnreadMessage}
      handleCloseChatChannel={handleCloseChatChannel}
      handleOpenChatChannel={handleOpenChatChannel}
    />
  );
});

export default ChatChannel;



// WEBPACK FOOTER //
// ./foody/src/pages/ChatBox/ChatChannel/ChatChannel.jsx